import { lazy, Suspense, useState, useCallback, useRef, useEffect } from "react";
import BottomTabBar from "@/components/BottomTabBar";
import StudioSidebar, {
  useSidebarCollapsedState,
  type StudioSection,
} from "@/components/StudioSidebar";
import { StudioModeErrorBoundary } from "@/components/StudioModeErrorBoundary";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
// framer-motion intentionally NOT eagerly imported here. The only motion
// usage on the Studio shell is the post-transform celebration toast, which
// is lazy-loaded below as `CelebrationToast` to keep motion-vendor out of
// the LCP critical path.
import Navbar from "@/components/Navbar";
import { personas, personaAvatars, type Persona } from "@/lib/personas";
import { useAuth } from "@/hooks/useAuth";
import { useCredits } from "@/hooks/useCredits";
import { supabase } from "@/integrations/supabase/client";
import { analytics } from "@/lib/analytics";
import { toast } from "sonner";
import { Copy, Check, Bookmark, Loader2, Paperclip, X, Columns2, ChevronLeft, ChevronRight, Zap, Lock, ArrowLeft, ThumbsUp, ThumbsDown, Download, WifiOff } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useKeyboardShortcuts, modifierKey } from "@/hooks/useKeyboardShortcuts";
import { useIsMobile } from "@/hooks/use-mobile";
import { getTransformLoadingButtonLabel } from "@/lib/transformButtonLabels";
import type { OutputType } from "@/components/CreatePanel";
import {
  savePendingInput,
  getPendingInput,
  clearPendingInput,
  readStoredCreateSubTab,
  writeStoredCreateSubTab,
  type StudioCreateSubTab,
  type StudioSelectedMode,
} from "@/lib/inputPersistence";
import { allowsImageTransforms } from "@/lib/plans";
import { formatRepurposeEmailDisplay } from "@/lib/repurposeEmailFormat";
import { appendGuestHistory } from "@/lib/transformHistory";
import { toFriendlyError, toFriendlyToast } from "@/lib/errorMessages";
import type { SidePanelLoadArgs } from "@/components/StudioSidePanel";
import {
  DEFAULT_WRITING_SETTINGS,
  type WritingSettings,
  type WriteCreativityOption,
} from "@/lib/writingSettings";

const UpgradeModal = lazy(() => import("@/components/UpgradeModal"));
const UnlockTrialModal = lazy(() => import("@/components/UnlockTrialModal"));
const WorkspacePanel = lazy(() => import("@/components/WorkspacePanel"));
const CreditBanner = lazy(() => import("@/components/CreditBanner"));
const ShareMenu = lazy(() => import("@/components/ShareMenu"));
const DownloadMenu = lazy(() => import("@/components/DownloadMenu"));
const StudioSidePanel = lazy(() => import("@/components/StudioSidePanel"));
const AICoachPanel = lazy(() => import("@/components/AICoachPanel"));
const RewriteMode = lazy(() => import("@/components/RewriteMode"));
const RepurposeMode = lazy(() => import("@/components/RepurposeMode"));
const ImagesMode = lazy(() => import("@/components/ImagesMode"));
const CelebrationToast = lazy(() => import("@/components/CelebrationToast"));

/** Warm all three mode chunks once Studio mounts so tab switches never
 *  fall back to a Suspense spinner after the first paint. Triggered on
 *  idle to keep the initial render cost unchanged. */
function prefetchStudioModeChunks(): void {
  void import("@/components/RewriteMode");
  void import("@/components/RepurposeMode");
  void import("@/components/ImagesMode");
}

import type { RewriteTab } from "@/components/RewriteMode";

const MAX_HISTORY = 5;

function readSidePanelOpenFromStorage(): boolean {
  try {
    return localStorage.getItem("vml_side_panel_open") === "1";
  } catch {
    return false;
  }
}

const REWRITE_TAB_STORAGE_KEY = "vml_rewrite_tab";
const VALID_REWRITE_TABS: RewriteTab[] = [
  "casual",
  "business",
  "polish",
  "expand",
  "shorten",
  "persuade",
];

function readStoredRewriteTab(): RewriteTab | null {
  try {
    const v = localStorage.getItem(REWRITE_TAB_STORAGE_KEY) as RewriteTab | null;
    if (v && VALID_REWRITE_TABS.includes(v)) return v;
  } catch {
    /* unavailable */
  }
  return null;
}

function writeStoredRewriteTab(tab: RewriteTab): void {
  try {
    localStorage.setItem(REWRITE_TAB_STORAGE_KEY, tab);
  } catch {
    /* quota exceeded — silent */
  }
}

function rewriteTabToLegacyState(tab: RewriteTab): {
  selectedMode: StudioSelectedMode;
  createSubTab: StudioCreateSubTab | null;
  writeTone: WriteTone | null;
} {
  if (tab === "casual" || tab === "business") {
    return { selectedMode: "create", createSubTab: tab, writeTone: null };
  }
  const toneMap: Record<Exclude<RewriteTab, "casual" | "business">, WriteTone> = {
    polish: "Polish",
    expand: "Expand",
    shorten: "Shorten",
    persuade: "Persuade",
  };
  return { selectedMode: "write", createSubTab: null, writeTone: toneMap[tab] };
}

/** Images personas (Create > Image): Da Vinci, Mozart, Picasso (display order). */
const CREATE_PERSONA_SLUGS = ["davinci", "mozart", "picasso"] as const;

type WriteTone = "Polish" | "Rewrite" | "Expand" | "Shorten" | "Persuade";

const STUDIO_CREATE_ORDER = ["casual", "business", "repurpose", "image"] as const;

const CREATE_SUB_TAB_LABELS: Record<(typeof STUDIO_CREATE_ORDER)[number], string> = {
  casual: "Casual",
  business: "Business",
  repurpose: "Repurpose",
  image: "Image",
};

const defaultSection: StudioCreateSubTab = "repurpose";

/** First-transform toast copy — never leaks legacy persona names into Repurpose / Write. */
function buildTextTransformCelebrationMessage(
  selectedMode: StudioSelectedMode,
  createSubTab: StudioCreateSubTab,
  repurposePlatformCount: number,
): string {
  if (selectedMode === "write") return "Nice. You just polished your writing.";
  if (selectedMode === "create") {
    if (createSubTab === "repurpose") {
      const n = Math.max(1, repurposePlatformCount);
      return `Done. Your content is ready for ${n} platform${n !== 1 ? "s" : ""}.`;
    }
    if (createSubTab === "casual" || createSubTab === "business") {
      return `Nice. Your ${CREATE_SUB_TAB_LABELS[createSubTab].toLowerCase()} rewrite is ready.`;
    }
    if (createSubTab === "image") return "Done. Your image is ready.";
  }
  return "Nice. Your writing just got polished.";
}

/** Create text sub-tabs that share the system-prompt-driven flow (no persona). */
const CREATE_TEXT_STYLES = ["casual", "business", "repurpose"] as const;
type CreateTextStyle = (typeof CREATE_TEXT_STYLES)[number];
const isCreateTextStyle = (s: string): s is CreateTextStyle =>
  (CREATE_TEXT_STYLES as readonly string[]).includes(s);

function mapWriteToneToApi(tone: WriteTone): string {
  if (tone === "Rewrite") return "Transform";
  return tone;
}

function writePrimaryLabel(tone: WriteTone): string {
  const labels: Record<WriteTone, string> = {
    Polish: "Polish it",
    Rewrite: "Rewrite this",
    Expand: "Make it longer",
    Shorten: "Cut the fluff",
    Persuade: "Make it sell",
  };
  return labels[tone];
}

function createTextStylePrimaryLabel(style: CreateTextStyle): string {
  if (style === "casual") return "Make it casual";
  if (style === "business") return "Make it business";
  return "Repurpose content";
}

function parseRepurposeOutput(output: string, platforms: string[]): Array<{ platform: string; content: string }> {
  const PLATFORM_LABELS: Record<string, string> = {
    linkedin: "LinkedIn",
    x: "X",
    email: "Email",
    instagram: "Instagram",
    youtube: "YouTube",
    facebook: "Facebook",
  };

  if (!output || !platforms || platforms.length === 0) {
    return [{ platform: "Output", content: output || "" }];
  }

  const labels = platforms.map((p) => PLATFORM_LABELS[p]).filter(Boolean);
  if (labels.length === 0) {
    return [{ platform: "Output", content: output }];
  }

  const escaped = labels.map((l) => l.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  // Tolerant: matches "## Label", "### Label", "**Label**", or bare "Label"
  const pattern = new RegExp(
    `^(?:#{1,6}\\s*|\\*\\*\\s*)?(${escaped.join("|")})(?:\\s*\\*\\*)?\\s*$`,
    "gim",
  );
  const matches: Array<{ platform: string; headerStart: number; headerEnd: number }> = [];
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(output)) !== null) {
    matches.push({ platform: m[1], headerStart: m.index, headerEnd: m.index + m[0].length });
  }

  if (matches.length === 0) {
    return [{ platform: "Output", content: output }];
  }

  const sections: Array<{ platform: string; content: string }> = [];
  for (let i = 0; i < matches.length; i++) {
    const contentStart = matches[i].headerEnd;
    const contentEnd = i + 1 < matches.length ? matches[i + 1].headerStart : output.length;
    const content = output.slice(contentStart, contentEnd).trim();
    sections.push({ platform: matches[i].platform, content });
  }

  return sections;
}

function parseWriteToneFromApi(raw: string): WriteTone {
  const map: Record<string, WriteTone> = {
    Transform: "Rewrite",
    Polish: "Polish",
    Expand: "Expand",
    Shorten: "Shorten",
    Persuade: "Persuade",
  };
  return map[raw] ?? "Rewrite";
}

const countWords = (s: string): number => {
  const t = s.trim();
  if (!t) return 0;
  return t.split(/\s+/).length;
};

interface ResultItem {
  id: string;
  inputSnippet: string;
  outputText?: string;
  /** Platform ids from Repurpose transform response (for multi-block output UI). */
  repurposePlatformIds?: string[];
  outputImageUrl?: string;
  referenceImageUrl?: string | null;
  persona: Persona;
  intensity: number;
  workType: "text" | "image";
  mode: StudioSelectedMode;
  tone: string;
  voiceProfileId?: string | null;
  timestamp: Date;
}

interface BrandVoiceContext {
  brandDescription: string;
  targetAudience: string;
  tonePreference: string;
  forbiddenWords: string;
  autoApplyBrandVoice: boolean;
}

function buildPendingPayload(
  inputText: string,
  referenceImage: string | null,
  selectedPersona: Persona,
  imagePersona: Persona | null,
  selectedMode: StudioSelectedMode,
  createSubTab: StudioCreateSubTab,
  createPrompt: string,
  createOutputType: OutputType,
) {
  // Prefer the explicitly-toggled Create > Image persona when the user was
  // in that tab so the selection survives the sign-up round trip.
  const personaSlug =
    selectedMode === "create" && createSubTab === "image" && imagePersona
      ? imagePersona.slug
      : selectedPersona.slug;
  return {
    text: inputText,
    image: referenceImage,
    personaSlug,
    intensity: 90,
    selectedMode,
    createSubTab,
    createPrompt,
    createOutputType,
  };
}

const Studio = () => {
  const [inputText, setInputText] = useState("");
  const [referenceImage, setReferenceImage] = useState<string | null>(null);
  /** Create > Image optional reference (data URL) for image-to-image */
  const [createImageReferenceDataUrl, setCreateImageReferenceDataUrl] = useState<string | null>(null);
  const [selectedPersona, setSelectedPersona] = useState<Persona>(personas.find((p) => p.slug === "hemingway") ?? personas[0]);
  // Create > Create Image tab persona selection. Null by default — the user
  // must pick one explicitly. Clicking the same card again deselects it.
  // Single-select: picking a new one replaces the previous.
  const [imagePersona, setImagePersona] = useState<Persona | null>(null);
  // Image generation options forwarded to imagen-generate. Aspect/mood are
  // passed through transparently — backend accepts unknown body fields.
  const [imageAspectRatio, setImageAspectRatio] = useState<string>("1:1");
  const [imageMoods, setImageMoods] = useState<string[]>([]);
  const [imageError, setImageError] = useState<string | null>(null);
  const [selectedMode, setSelectedMode] = useState<StudioSelectedMode>(() => {
    const hasStoredRewritePreference = readStoredRewriteTab() !== null;
    const hasStoredCreatePreference = readStoredCreateSubTab() !== null;
    if (!hasStoredRewritePreference && !hasStoredCreatePreference) return "create";
    return hasStoredCreatePreference ? "create" : "write";
  });
  // First-time visitors land on Repurpose (the marquee feature). Returning
  // users keep their last-selected sub-tab from localStorage.
  const [createSubTab, setCreateSubTab] = useState<StudioCreateSubTab>(
    () => readStoredCreateSubTab() ?? defaultSection,
  );
  const [writeTone, setWriteTone] = useState<WriteTone>("Polish");
  const [rewriteTab, setRewriteTabState] = useState<RewriteTab>(() => readStoredRewriteTab() ?? "polish");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingType, setLoadingType] = useState<"text" | "image">("text");
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);

  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [writingCreativity, setWritingCreativity] = useState<WriteCreativityOption>(
    DEFAULT_WRITING_SETTINGS.creativity,
  );
  const handleWritingSettingsChange = useCallback((s: WritingSettings) => {
    setWritingCreativity(s.creativity);
  }, []);
  const [brandVoiceContext, setBrandVoiceContext] = useState<BrandVoiceContext>({
    brandDescription: "",
    targetAudience: "",
    tonePreference: "Polish",
    forbiddenWords: "",
    autoApplyBrandVoice: false,
  });
  const [activeTab, setActiveTab] = useState<"studio" | "saved" | "ai-coach">("studio");
  const [celebrationOpen, setCelebrationOpen] = useState(false);
  const [celebrationMessage, setCelebrationMessage] = useState("");
  const [showCreatePulse, setShowCreatePulse] = useState(false);
  const [createPrompt, setCreatePrompt] = useState("");
  const [createOutputType, setCreateOutputType] = useState<OutputType>("writing");
  const [savedRefreshKey, setSavedRefreshKey] = useState(0);
  const [results, setResults] = useState<ResultItem[]>([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);
  const [compareMode, setCompareMode] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [feedbackByResultId, setFeedbackByResultId] = useState<Record<string, "up" | "down">>({});
  const [isOnline, setIsOnline] = useState(() => (typeof navigator !== "undefined" ? navigator.onLine : true));
  const [offlineBannerPhase, setOfflineBannerPhase] = useState<"hidden" | "shown" | "hiding">(() =>
    typeof navigator !== "undefined" && !navigator.onLine ? "shown" : "hidden",
  );
  // Side panel (History & Settings) — open state persisted in localStorage.
  const [sidePanelOpen, setSidePanelOpen] = useState<boolean>(readSidePanelOpenFromStorage);
  /** Defer loading the side-panel chunk until first open (or persisted open). */
  const [sidePanelChunkMounted, setSidePanelChunkMounted] = useState<boolean>(readSidePanelOpenFromStorage);
  // Bumped after each successful transform so the panel refreshes history.
  const [sidePanelRefreshKey, setSidePanelRefreshKey] = useState(0);

  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { credits, fetchCredits, setBalance, loading: creditsLoading } = useCredits();
  const isCoarsePointer = useIsMobile();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const restoredRef = useRef(false);
  const personaParamAppliedRef = useRef(false);
  const projectParamAppliedRef = useRef(false);
  const prevModeRef = useRef<StudioSelectedMode>(selectedMode);
  // Synchronous re-entry guard for transform handlers. React's `setIsLoading`
  // is async; without a ref-based guard, a fast double-tap can fire two
  // Edge Function calls before the button's `disabled` state propagates,
  // causing double credit deduction. One ref serves both handleTransform and
  // handleVisualize because the user can only submit one tab at a time.
  const inFlightRef = useRef(false);

  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarCollapsedState();

  // Warm the Rewrite/Repurpose/Images chunks on idle so switching between
  // the three top-level tabs doesn't flash a Suspense spinner.
  useEffect(() => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(prefetchStudioModeChunks, { timeout: 2000 });
      return () => {
        const cancel = (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback;
        if (typeof cancel === "function") cancel(id);
      };
    }
    const t = window.setTimeout(prefetchStudioModeChunks, 600);
    return () => window.clearTimeout(t);
  }, []);

  const currentSection: StudioSection = (() => {
    if (activeTab === "saved") return "workspace";
    if (activeTab === "ai-coach") return "ai-coach";
    if (selectedMode === "create" && createSubTab === "image") return "images";
    if (selectedMode === "create" && createSubTab === "repurpose") return "repurpose";
    return "rewrite";
  })();

  const setRewriteTab = useCallback((tab: RewriteTab) => {
    setRewriteTabState(tab);
    writeStoredRewriteTab(tab);
    const { selectedMode: nextMode, createSubTab: nextSub, writeTone: nextTone } = rewriteTabToLegacyState(tab);
    setSelectedMode(nextMode);
    if (nextSub) setCreateSubTab(nextSub);
    if (nextTone) setWriteTone(nextTone);
  }, []);

  // One-time sync: keep persisted section preferences. First-time users with
  // no preference land on Repurpose (create + repurpose).
  useEffect(() => {
    const storedRewriteTab = readStoredRewriteTab();
    const storedCreateSubTab = readStoredCreateSubTab();

    if (storedRewriteTab) {
      const { selectedMode: m, createSubTab: s, writeTone: t } = rewriteTabToLegacyState(storedRewriteTab);
      setSelectedMode(m);
      if (s) setCreateSubTab(s);
      if (t) setWriteTone(t);
      return;
    }

    if (storedCreateSubTab) {
      setSelectedMode("create");
      setCreateSubTab(storedCreateSubTab);
      return;
    }

    setSelectedMode("create");
    setCreateSubTab(defaultSection);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-shot mount sync
  }, []);

  const selectSection = useCallback(
    (next: StudioSection) => {
      if (next === "workspace") {
        setActiveTab("saved");
        return;
      }
      if (next === "ai-coach") {
        setActiveTab("ai-coach");
        return;
      }
      setActiveTab("studio");
      if (next === "rewrite") {
        // Re-apply the last-used rewrite tab so the underlying mode/subtab match.
        const { selectedMode: nextMode, createSubTab: nextSub, writeTone: nextTone } =
          rewriteTabToLegacyState(rewriteTab);
        setSelectedMode(nextMode);
        if (nextSub) setCreateSubTab(nextSub);
        if (nextTone) setWriteTone(nextTone);
        return;
      }
      if (next === "repurpose") {
        setSelectedMode("create");
        setCreateSubTab("repurpose");
        return;
      }
      if (next === "images") {
        setSelectedMode("create");
        setCreateSubTab("image");
      }
    },
    [rewriteTab],
  );

  const openCoach = useCallback(() => {
    setActiveTab("ai-coach");
  }, []);




  const latestResult = results.length > 0 ? results[Math.min(currentResultIndex, results.length - 1)] : null;
  const writeOutputText =
    latestResult?.mode === "write" && latestResult.workType === "text"
      ? latestResult.outputText ?? null
      : null;
  const createOutputText =
    latestResult?.mode === "create" && latestResult.workType === "text"
      ? latestResult.outputText ?? null
      : null;

  const createImageOutputUrl =
    latestResult?.mode === "create" &&
    latestResult.workType === "image" &&
    latestResult.outputImageUrl
      ? latestResult.outputImageUrl
      : null;

  const createRepurposeSections =
    latestResult?.mode === "create" &&
    latestResult.workType === "text" &&
    latestResult.repurposePlatformIds &&
    latestResult.repurposePlatformIds.length > 0 &&
    latestResult.outputText
      ? parseRepurposeOutput(latestResult.outputText, latestResult.repurposePlatformIds)
      : null;

  const isImageAction =
    selectedMode === "write" && Boolean(referenceImage)
      ? true
      : selectedMode === "create" && createSubTab === "image";

  const hasTextInput = inputText.trim().length > 0;
  const hasImageInput = Boolean(referenceImage);

  useEffect(() => {
    const onOff = () => {
      setIsOnline(false);
      setOfflineBannerPhase("shown");
    };
    const onOn = () => {
      setIsOnline(true);
      setOfflineBannerPhase((p) => (p === "shown" ? "hiding" : "hidden"));
    };
    window.addEventListener("offline", onOff);
    window.addEventListener("online", onOn);
    return () => {
      window.removeEventListener("offline", onOff);
      window.removeEventListener("online", onOn);
    };
  }, []);

  useEffect(() => {
    if (offlineBannerPhase !== "hiding") return;
    const t = window.setTimeout(() => setOfflineBannerPhase("hidden"), 200);
    return () => window.clearTimeout(t);
  }, [offlineBannerPhase]);

  // Handle ?tab=ai-coach | ?tab=saved (legacy) and ?section=<id> (new sidebar/bottom-tab navigation
  // from outside Studio) — switch state and clean the URL.
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "ai-coach" || tabParam === "saved") {
      setActiveTab(tabParam);
      const url = new URL(window.location.href);
      url.searchParams.delete("tab");
      window.history.replaceState({}, "", url.toString());
    }
    const sectionParam = searchParams.get("section");
    if (sectionParam) {
      const valid: StudioSection[] = ["rewrite", "repurpose", "images", "workspace", "ai-coach"];
      if ((valid as string[]).includes(sectionParam)) {
        selectSection(sectionParam as StudioSection);
      }
      const url = new URL(window.location.href);
      url.searchParams.delete("section");
      window.history.replaceState({}, "", url.toString());
    }
  }, [searchParams, selectSection]);

  // Path-based section sync — keeps the active Studio section aligned with
  // the current top-level route (/rewrite, /repurpose, /images, /coach,
  // /workspace). Re-runs on every pathname change so back/forward + bottom
  // tab taps stay in sync.
  useEffect(() => {
    const map: Record<string, StudioSection> = {
      "/rewrite": "rewrite",
      "/repurpose": "repurpose",
      "/images": "images",
      "/coach": "ai-coach",
      "/workspace": "workspace",
    };
    const next = map[location.pathname];
    if (next && next !== currentSection) {
      selectSection(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to pathname
  }, [location.pathname]);

  useEffect(() => {
    if (personaParamAppliedRef.current) return;
    const slug = searchParams.get("persona");
    if (slug) {
      const found = personas.find((p) => p.slug === slug);
      if (found) {
        setSelectedPersona(found);
        if (CREATE_PERSONA_SLUGS.includes(found.slug as (typeof CREATE_PERSONA_SLUGS)[number])) {
          setSelectedMode("create");
          setCreateSubTab("image");
          setImagePersona(found);
        }
      }
    }
    const textParam = searchParams.get("text");
    if (textParam) {
      try {
        setInputText(decodeURIComponent(textParam));
      } catch {
        setInputText(textParam);
      }
    }
    personaParamAppliedRef.current = true;
  }, [searchParams]);

  useEffect(() => {
    if (projectParamAppliedRef.current || !user) return;

    const projectId = searchParams.get("project");
    const itemId = searchParams.get("item");
    if (!projectId && !itemId) {
      projectParamAppliedRef.current = true;
      return;
    }

    let cancelled = false;
    (async () => {
      if (projectId) {
        const { data: project } = await supabase
          .from("projects")
          .select("id, default_mode, default_persona, default_voice_profile_id")
          .eq("id", projectId)
          .maybeSingle();

        if (!cancelled && project) {
          setSelectedMode(project.default_mode === "create" ? "create" : "write");
          const personaFromProject = personas.find((p) => p.slug === project.default_persona);
          if (personaFromProject) setSelectedPersona(personaFromProject);
        }
      }

      if (itemId) {
        const { data: item } = await supabase
          .from("project_items")
          .select("input_text,output_text,mode,persona,tone,voice_profile_id")
          .eq("id", itemId)
          .maybeSingle();

        if (!cancelled && item) {
          setInputText(item.input_text ?? "");
          setReferenceImage(null);
          const normalizedMode: StudioSelectedMode = item.mode === "create" ? "create" : "write";
          setSelectedMode(normalizedMode);
          const itemPersona = personas.find((p) => p.slug === item.persona);
          if (itemPersona) setSelectedPersona(itemPersona);

          setWriteTone(item.mode === "code" ? "Polish" : parseWriteToneFromApi(item.tone));

          if (item.output_text) {
            setResults([
              {
                id: crypto.randomUUID(),
                inputSnippet: item.input_text ?? "",
                outputText: item.output_text,
                persona: itemPersona ?? selectedPersona,
                intensity: 90,
                workType: "text",
                mode: normalizedMode,
                tone: item.tone ?? "Transform",
                voiceProfileId: item.voice_profile_id,
                timestamp: new Date(),
              },
            ]);
            setCurrentResultIndex(0);
          }
        }
      }

      projectParamAppliedRef.current = true;
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, user, selectedPersona]);

  useEffect(() => {
    if (!user || restoredRef.current) return;
    const pending = getPendingInput();
    if (pending) {
      restoredRef.current = true;
      setInputText(pending.text);
      if (pending.image) setReferenceImage(pending.image);
      setCreatePrompt(pending.createPrompt);
      setCreateOutputType(pending.createOutputType as OutputType);
      setSelectedMode(pending.selectedMode);
      setCreateSubTab(pending.createSubTab);
      if (pending.personaSlug) {
        const found = personas.find((p) => p.slug === pending.personaSlug);
        if (found) {
          setSelectedPersona(found);
          // If the user was in Create > Image when they bounced off for auth,
          // restore the toggle selection too.
          if (
            pending.selectedMode === "create" &&
            pending.createSubTab === "image" &&
            CREATE_PERSONA_SLUGS.includes(found.slug as (typeof CREATE_PERSONA_SLUGS)[number])
          ) {
            setImagePersona(found);
          }
        }
      }
      clearPendingInput();
      toast.success("Your content has been restored — ready to transform");
      return;
    }

    // Studio Defaults editor was removed when /settings consolidated into
    // Workspace > Settings. Stop reading user_preferences from here — defaults
    // come from component-local initial state instead.
    restoredRef.current = true;
  }, [user]);

  useEffect(() => {
    if (!user) {
      setBrandVoiceContext({
        brandDescription: "",
        targetAudience: "",
        tonePreference: "Polish",
        forbiddenWords: "",
        autoApplyBrandVoice: false,
      });
      return;
    }
    let cancelled = false;
    (async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data } = await supabase.from("brand_context" as any).select("*").eq("user_id", user.id).maybeSingle();
      if (cancelled) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const row = data as any;
      if (!row) return;
      const hasSavedFields = Boolean(
        (row.brand_description ?? "").trim() ||
          (row.target_audience ?? "").trim() ||
          (row.forbidden_words ?? "").trim(),
      );
      setBrandVoiceContext({
        brandDescription: row.brand_description ?? "",
        targetAudience: row.target_audience ?? "",
        tonePreference: row.tone_preference ?? "Polish",
        forbiddenWords: row.forbidden_words ?? "",
        autoApplyBrandVoice:
          typeof row.auto_apply_brand_voice === "boolean"
            ? row.auto_apply_brand_voice
            : hasSavedFields,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    // Track mode transitions. The Create > Image tab now defaults to no
    // persona selected — users must pick one explicitly via the card toggle.
    // No automatic persona assignment happens on mode change.
    prevModeRef.current = selectedMode;
  }, [selectedMode]);

  useEffect(() => {
    if (createSubTab !== "image") setCreateImageReferenceDataUrl(null);
  }, [createSubTab]);

  useEffect(() => {
    writeStoredCreateSubTab(createSubTab);
  }, [createSubTab]);

  // Persist side panel open state.
  useEffect(() => {
    try {
      localStorage.setItem("vml_side_panel_open", sidePanelOpen ? "1" : "0");
    } catch {
      // quota exceeded — ignore
    }
  }, [sidePanelOpen]);

  useEffect(() => {
    if (sidePanelOpen) setSidePanelChunkMounted(true);
  }, [sidePanelOpen]);

  const handleSidePanelLoad = useCallback(
    ({ item }: SidePanelLoadArgs) => {
      setInputText(item.inputSnippet);
      setResults([]);
      setCurrentResultIndex(0);
      if (item.mode === "write" || item.mode === "create") {
        setSelectedMode(item.mode);
      } else {
        setSelectedMode("write");
      }
      if (item.mode === "create" && item.createSubTab) {
        setCreateSubTab(item.createSubTab);
      }
      if (item.personaSlug) {
        const found = personas.find((p) => p.slug === item.personaSlug);
        if (found) {
          setSelectedPersona(found);
          if (
            item.mode === "create" &&
            item.createSubTab === "image" &&
            CREATE_PERSONA_SLUGS.includes(found.slug as (typeof CREATE_PERSONA_SLUGS)[number])
          ) {
            setImagePersona(found);
          }
        }
      }
      toast.success("Loaded from history");
    },
    [],
  );

  const autoSave = useCallback(
    async (
      opts: {
        workType: "text" | "image";
        inputSnippet: string;
        outputText?: string;
        outputImageUrl?: string;
        personaOverride?: Persona;
      },
    ) => {
      if (!user) return;
      const p = opts.personaOverride ?? selectedPersona;
      try {
        await supabase.from("saved_works").insert({
          user_id: user.id,
          persona_slug: p.slug,
          persona_name: p.name,
          intensity: 90,
          input_snippet: opts.inputSnippet.slice(0, 300),
          output_text: opts.outputText?.slice(0, 2000) ?? null,
          output_image_url: opts.outputImageUrl ?? null,
          work_type: opts.workType,
          is_favorite: false,
        });
        setSavedRefreshKey((k) => k + 1);
      } catch (err) {
        console.error("[autoSave] insert failed", err);
        toast.error("Failed to save — please try again");
      }
    },
    [user, selectedPersona],
  );

  const canAfford = (cost: number): boolean => {
    if (!credits || credits.balance < cost) {
      savePendingInput(
        buildPendingPayload(inputText, referenceImage, selectedPersona, imagePersona, selectedMode, createSubTab, createPrompt, createOutputType),
      );
      if (credits?.tier === "trial") {
        setUnlockOpen(true);
      } else {
        setUpgradeOpen(true);
      }
      return false;
    }
    return true;
  };

  const getImageBase64 = async (): Promise<string | null> => {
    if (!referenceImage) return null;
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = reject;
        img.src = referenceImage;
      });
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(img, 0, 0);
      const dataUrl = canvas.toDataURL("image/png");
      return dataUrl.split(",")[1] || null;
    } catch {
      return null;
    }
  };

  const getCreateReferenceBase64 = (): string | null => {
    if (!createImageReferenceDataUrl) return null;
    const [, base64] = createImageReferenceDataUrl.split(",", 2);
    return base64 || null;
  };

  const addResult = (item: Omit<ResultItem, "id" | "timestamp">) => {
    setSaved(false);
    setCopied(false);
    setResults((prev) => {
      const next = [...prev, { ...item, id: crypto.randomUUID(), timestamp: new Date() }];
      const trimmed = next.slice(-MAX_HISTORY);
      setCurrentResultIndex(trimmed.length - 1);
      return trimmed;
    });
  };

  const handleTransform = async (overridePersona?: Persona) => {
    if (inFlightRef.current) return; // synchronous re-entry guard
    if (!inputText.trim()) return;
    // Repurpose charges 1 credit per selected platform — gate the upgrade
    // prompt on the actual cost so a user with 1 credit and 3 platforms
    // sees the upgrade modal instead of a server-side "insufficient" error.
    const transformCost =
      selectedMode === "create" && createSubTab === "repurpose"
        ? Math.max(1, selectedPlatforms.length)
        : 1;
    if (!canAfford(transformCost)) return;
    inFlightRef.current = true;
    setIsLoading(true);
    setLoadingType("text");

    // Rewrite flow no longer uses personas — it runs with the generic
    // professional writer system prompt on the server. The `persona` field
    // on the request is omitted entirely when selectedMode is write.
    const isWriteText = selectedMode === "write" && !overridePersona;
    const personaForRequest = overridePersona ?? selectedPersona;
    const apiTone = mapWriteToneToApi(writeTone);
    const isEligibleBrandVoiceTransform =
      selectedMode === "write" || (selectedMode === "create" && createSubTab === "repurpose");
    const hasBrandVoiceContext = Boolean(
      brandVoiceContext.brandDescription.trim() ||
        brandVoiceContext.targetAudience.trim() ||
        brandVoiceContext.forbiddenWords.trim(),
    );
    const autoApplyBrandVoice =
      isEligibleBrandVoiceTransform &&
      credits?.tier === "pro" &&
      brandVoiceContext.autoApplyBrandVoice &&
      hasBrandVoiceContext;
    const brandVoicePrefix = autoApplyBrandVoice
      ? [
          "Brand Voice Context:",
          brandVoiceContext.brandDescription.trim()
            ? `Brand Description: ${brandVoiceContext.brandDescription.trim()}`
            : "",
          brandVoiceContext.targetAudience.trim()
            ? `Target Audience: ${brandVoiceContext.targetAudience.trim()}`
            : "",
          brandVoiceContext.tonePreference.trim()
            ? `Tone Preference: ${brandVoiceContext.tonePreference.trim()}`
            : "",
          brandVoiceContext.forbiddenWords.trim()
            ? `Forbidden Words: ${brandVoiceContext.forbiddenWords.trim()}`
            : "",
        ]
          .filter(Boolean)
          .join("\n")
      : "";
    const transformInputText = brandVoicePrefix ? `${brandVoicePrefix}\n\n${inputText}` : inputText;

    analytics.transformStarted(isWriteText ? "write-mode" : personaForRequest.slug, 90);

    try {
      const reference_image_base64 = await getImageBase64();
      const { data: { session } } = await supabase.auth.getSession();
      // Create text sub-tabs (Casual / Business / Repurpose) drive the
      // system prompt via create_text_style — no persona on the wire for those.
      const isCreateTextStyleRequest =
        selectedMode === "create" && isCreateTextStyle(createSubTab);
      const { data, error } = await supabase.functions.invoke("claude-transform", {
        body: {
          input_text: transformInputText,
          mode: selectedMode,
          tone: apiTone,
          // Omit persona for rewrite flow and for Create text-style tabs (including Repurpose).
          ...(isWriteText || (selectedMode === "create" && isCreateTextStyleRequest)
            ? {}
            : { persona: personaForRequest.slug }),
          intensity: 90,
          reference_image_base64,
          create_text_style: isCreateTextStyleRequest ? createSubTab : undefined,
          ...(createSubTab === "repurpose" && selectedMode === "create"
            ? {
                platforms: selectedPlatforms,
                platform_count: selectedPlatforms.length,
              }
            : {}),
          ...(autoApplyBrandVoice
            ? {
                auto_apply_brand_voice: true,
                brand_context: {
                  brand_description: brandVoiceContext.brandDescription.trim(),
                  target_audience: brandVoiceContext.targetAudience.trim(),
                  tone_preference: brandVoiceContext.tonePreference.trim(),
                  forbidden_words: brandVoiceContext.forbiddenWords.trim(),
                },
              }
            : {}),
          // Creativity preset (Steady / Sharp / Bold) — sent for Rewrite and
          // Repurpose so the Edge Function can shape the system prompt.
          ...(isWriteText ||
          (selectedMode === "create" && createSubTab === "repurpose")
            ? { creativity: writingCreativity }
            : {}),
        },
        headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : undefined,
      });
      if (error) throw error;

      if (!data || typeof data.output !== "string" || !data.output.trim()) {
        throw new Error("Transform produced no output");
      }

      const repurposeIds =
        Array.isArray((data as { platforms?: unknown }).platforms) &&
        (data as { platforms: unknown[] }).platforms.every((p) => typeof p === "string")
          ? ((data as { platforms: string[] }).platforms)
          : undefined;

      addResult({
        inputSnippet: inputText,
        outputText: data.output,
        repurposePlatformIds: repurposeIds,
        referenceImageUrl: referenceImage,
        persona: personaForRequest,
        intensity: 90,
        workType: "text",
        mode: selectedMode,
        tone: writeTone,
      });
      // Server already deducted (only on success) — sync client balance from response.
      if (typeof data.credits_remaining === "number") {
        setBalance(data.credits_remaining);
      } else {
        await fetchCredits();
      }
      analytics.transformCompleted(personaForRequest.slug, 90);
      autoSave({ workType: "text", inputSnippet: inputText, outputText: data.output, personaOverride: personaForRequest });
      // Persona containment: only surface a persona name from the Create
      // Image tab — Write and Create text sub-tabs (casual / business
      // / repurpose) are system-prompt-driven. None of them should reference a
      // persona by name elsewhere in the UI.
      maybeFireFirstTransformCelebration(
        buildTextTransformCelebrationMessage(selectedMode, createSubTab, selectedPlatforms.length),
      );
      markTransformProgress(selectedMode);
      // Guest transform history — auth users read from saved_works.
      if (!user) {
        appendGuestHistory({
          inputSnippet: inputText,
          outputText: data.output,
          mode: selectedMode,
          createSubTab: selectedMode === "create" ? createSubTab : undefined,
          personaSlug: isWriteText ? undefined : personaForRequest.slug,
          personaName: isWriteText ? undefined : personaForRequest.name,
        });
      }
      setSidePanelRefreshKey((k) => k + 1);
      const newBalance = typeof data.credits_remaining === "number" ? data.credits_remaining : (credits?.balance ?? 0) - 1;
      if (newBalance <= 5) toast.warning(`Credits running low — ${newBalance} left`);
    } catch (err: unknown) {
      console.error("[handleTransform] failed", err);
      // Server only deducts on a successful, non-empty response, so a thrown
      // error here means no credit was charged. Communicate that clearly.
      toast.error(`${toFriendlyToast(err, "text")} Credit refunded.`);
      // Resync credits as a safety net in case the server state changed.
      await fetchCredits();
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  };

  const handleVisualize = async (overridePersona?: Persona) => {
    if (inFlightRef.current) return; // synchronous re-entry guard
    if (credits?.tier && credits.tier !== "trial" && !allowsImageTransforms(credits.tier)) {
      toast.error("Image transforms require Starter plan or above");
      setUpgradeOpen(true);
      return;
    }
    const imagenMode = selectedMode === "create" && createSubTab === "image" ? "create" : "transform";
    if (imagenMode === "transform" && !referenceImage) return;
    if (imagenMode === "create" && !inputText.trim()) return;
    if (!canAfford(2)) return;
    // In Create > Image, persona is OPTIONAL — use imagePersona if toggled, else null
    // for neutral output. In rewrite image-reference visualize, fall back to
    // selectedPersona (legacy behavior — persona is hidden from the Write UI).
    const personaForImage: Persona | null =
      overridePersona ?? (imagenMode === "create" ? imagePersona : selectedPersona);
    // Synthetic persona used only for local history/autoSave display when the
    // user generated neutrally (no persona picked). Never sent to the backend.
    const neutralPersona: Persona = {
      name: "Freeform",
      slug: "none",
      emoji: "✨",
      accentColor: "#6366F1",
    };
    const personaForRecord: Persona = personaForImage ?? neutralPersona;
    inFlightRef.current = true;
    setIsLoading(true);
    setLoadingType("image");
    analytics.transformStarted(personaForRecord.slug, 90);

    try {
      const reference_image_base64 =
        imagenMode === "transform" ? await getImageBase64() : getCreateReferenceBase64();
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke("imagen-generate", {
        body: {
          prompt: inputText,
          custom_prompt: inputText || undefined,
          ...(personaForImage ? { persona_slug: personaForImage.slug } : {}),
          intensity: 90,
          reference_image_base64,
          mode: imagenMode,
          aspect_ratio: imageAspectRatio,
          ...(imageMoods.length ? { moods: imageMoods } : {}),
        },
        headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : undefined,
      });
      if (error) throw error;
      // Structured error from imagen-generate: { error: "user-friendly message" }
      if (data && typeof (data as { error?: unknown }).error === "string") {
        setImageError((data as { error: string }).error);
        setIsLoading(false);
        return;
      }

      if (data.image_url) {
        addResult({
          inputSnippet: inputText || "Image transform",
          outputImageUrl: data.image_url,
          referenceImageUrl: referenceImage,
          persona: personaForRecord,
          intensity: 90,
          workType: "image",
          mode: selectedMode,
          tone: writeTone,
        });
        autoSave({ workType: "image", inputSnippet: inputText || "Image transform", outputImageUrl: data.image_url, personaOverride: personaForRecord });
        maybeFireFirstTransformCelebration(
          imagenMode === "create"
            ? personaForImage
              ? `Done. ${personaForImage.name}'s vision is ready.`
              : "Done. Your image is ready."
            : "Nice. You just polished your writing.",
        );
        markTransformProgress(selectedMode);
        if (!user) {
          appendGuestHistory({
            inputSnippet: inputText || "Image transform",
            outputImageUrl: data.image_url,
            mode: selectedMode,
            createSubTab: selectedMode === "create" ? createSubTab : undefined,
            personaSlug: personaForRecord.slug,
            personaName: personaForRecord.name,
          });
        }
        setSidePanelRefreshKey((k) => k + 1);
      } else {
        toast.error("No image returned");
      }
      // Server already deducted — sync client balance from response.
      if (typeof data.credits_remaining === "number") {
        setBalance(data.credits_remaining);
      } else {
        await fetchCredits();
      }
      analytics.transformCompleted(personaForRecord.slug, 90);
      const newBalance = typeof data.credits_remaining === "number" ? data.credits_remaining : (credits?.balance ?? 0) - 2;
      if (newBalance <= 5) toast.warning(`Credits running low — ${newBalance} left`);
    } catch (err: unknown) {
      console.error("[handleVisualize] failed", err);
      const friendly = toFriendlyError(err, "image");
      // Inline alert gets the subtitle (richer copy); toast stays terse.
      setImageError(friendly.subtitle);
      toast.error(friendly.title);
      await fetchCredits();
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    if (isImageAction) {
      handleVisualize();
    } else {
      handleTransform();
    }
  };

  const isCreateImageAction =
    isImageAction && selectedMode === "create" && createSubTab === "image";

  const isSubmitDisabled =
    !isOnline ||
    isLoading ||
    (isImageAction
      ? isCreateImageAction
        ? !hasTextInput
        : !hasImageInput
      : !hasTextInput);

  const submitIdleLabel = (() => {
    if (isImageAction) {
      if (isCreateImageAction) {
        if (!hasTextInput) return "Describe your image";
        return imagePersona ? `Generate with ${imagePersona.name}` : "Generate Image";
      }
      return "Transform image";
    }
    if (selectedMode === "create" && isCreateTextStyle(createSubTab)) {
      return createTextStylePrimaryLabel(createSubTab);
    }
    return writePrimaryLabel(writeTone);
  })();

  const submitLoadingLabel = getTransformLoadingButtonLabel({
    selectedMode,
    createSubTab,
    repurposePlatformCount: selectedPlatforms.length,
    isCreateImageAction,
    isImageTransform: isImageAction,
  });

  const showDesktopShortcutHints = !isCoarsePointer;
  const transformShortcutTitle = showDesktopShortcutHints
    ? `Shortcut: ${modifierKey}+Enter`
    : !isOnline
      ? "Reconnect to transform"
      : undefined;
  const clearShortcutTitle = showDesktopShortcutHints ? `Shortcut: ${modifierKey}+K` : undefined;
  const copyShortcutTitle = showDesktopShortcutHints ? `Shortcut: ${modifierKey}+Shift+C` : undefined;
  const saveShortcutTitle = showDesktopShortcutHints ? `Shortcut: ${modifierKey}+S` : undefined;

  const handleCopy = async () => {
    const text = latestResult?.outputText;
    if (!text) {
      toast.error("Nothing to copy");
      return;
    }
    let success = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        success = true;
      }
    } catch (err) {
      console.warn("Clipboard API failed, trying fallback", err);
    }
    if (!success) {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.top = "0";
        textarea.style.left = "0";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        success = document.execCommand("copy");
        document.body.removeChild(textarea);
      } catch (err) {
        console.warn("execCommand fallback failed", err);
      }
    }
    if (success) {
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Unable to copy — please select and copy manually");
    }
  };

  const handleCopyRepurposeSection = async (text: string) => {
    if (!text.trim()) {
      toast.error("Nothing to copy");
      return;
    }
    let success = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        success = true;
      }
    } catch (err) {
      console.warn("Clipboard API failed, trying fallback", err);
    }
    if (!success) {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.top = "0";
        textarea.style.left = "0";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        success = document.execCommand("copy");
        document.body.removeChild(textarea);
      } catch (err) {
        console.warn("execCommand fallback failed", err);
      }
    }
    if (success) toast.success("Copied to clipboard");
    else toast.error("Unable to copy — please select and copy manually");
  };

  const handleSave = () => {
    if (!user) {
      setUnlockOpen(true);
      return;
    }
    if (!latestResult) return;
    autoSave({
      workType: latestResult.workType,
      inputSnippet: latestResult.inputSnippet,
      outputText: latestResult.outputText,
      outputImageUrl: latestResult.outputImageUrl,
      personaOverride: latestResult.persona,
    });
    setSaved(true);
    markSaveProgress();
    toast.success("Saved to Workspace");
  };

  const handleFeedback = async (rating: "up" | "down") => {
    if (!latestResult) return;
    const resultId = latestResult.id;
    const previous = feedbackByResultId[resultId];
    if (previous === rating) return; // already rated this way

    // Optimistic update — UI feels instant.
    setFeedbackByResultId((prev) => ({ ...prev, [resultId]: rating }));

    try {
      const row = {
        user_id: user?.id ?? null,
        result_id: resultId,
        persona: latestResult.persona.slug,
        mode: latestResult.mode ?? selectedMode,
        rating,
      };
      // Signed-in users get upsert (one row per result, can switch up→down).
      // Guests append fresh rows (no unique constraint when user_id is NULL).
      if (user?.id) {
        const { error } = await supabase
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- table not in generated types yet
          .from("transform_feedback" as any)
          .upsert(row, { onConflict: "user_id,result_id" });
        if (error) throw error;
      } else {
        const { error } = await supabase
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- table not in generated types yet
          .from("transform_feedback" as any)
          .insert(row);
        if (error) throw error;
      }
    } catch (err) {
      // Fail-soft: keep the optimistic UI but log the error.
      // The migration may not be applied yet on this environment.
      console.warn("[transform_feedback] insert failed", err);
    }
  };

  const handleClearInput = useCallback(() => {
    setInputText("");
    if (referenceImage?.startsWith("blob:")) URL.revokeObjectURL(referenceImage);
    setReferenceImage(null);
    setTimeout(() => {
      const ta = document.getElementById("studio-input") as HTMLTextAreaElement | null;
      ta?.focus();
    }, 0);
  }, [referenceImage]);

  useKeyboardShortcuts({
    onSubmit: () => {
      if (!isSubmitDisabled) handleSubmit();
    },
    onClear: handleClearInput,
    onCopy: () => {
      if (latestResult?.outputText) void handleCopy();
    },
    onSave: () => {
      if (latestResult) handleSave();
    },
  });

  const handleRetransform = (persona: Persona) => {
    const imageBranch =
      (selectedMode === "write" && Boolean(referenceImage)) || (selectedMode === "create" && createSubTab === "image");
    setSelectedPersona(persona);
    // Keep the Create > Image toggle UI in sync when the user retries with a
    // different voice via the "Try another" pills.
    if (selectedMode === "create" && createSubTab === "image") {
      setImagePersona(persona);
    }
    setTimeout(() => {
      if (imageBranch) {
        void handleVisualize(persona);
      } else {
        void handleTransform(persona);
      }
    }, 100);
  };

  const handleDownload = async () => {
    if (!latestResult?.outputImageUrl) return;
    const ts = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const filename = `vinci-${latestResult.persona.slug}-${ts}.png`;
    try {
      // Always use blob URL + programmatic <a download> click for true file download.
      // Works on desktop browsers, mobile Chrome, and iOS Safari.
      const res = await fetch(latestResult.outputImageUrl, { mode: "cors" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      // Small delay before cleanup so the browser has time to start the download.
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      }, 200);
      toast.success("Image saved");
    } catch {
      // Fallback: open image in a new tab so the user can long-press to save.
      try {
        window.open(latestResult.outputImageUrl, "_blank", "noopener");
        toast("Image opened in a new tab — long press to save", { duration: 5000 });
      } catch {
        toast.error("Download failed — try again");
      }
    }
  };

  const handleCopyImage = async () => {
    if (!latestResult?.outputImageUrl) return;
    try {
      const res = await fetch(latestResult.outputImageUrl, { mode: "cors" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      // Use Clipboard API ClipboardItem to copy image blob
      const item = new ClipboardItem({ [blob.type || "image/png"]: blob });
      await navigator.clipboard.write([item]);
      toast.success("Image copied");
    } catch (err) {
      console.warn("Image copy failed", err);
      toast.error("Failed to copy image");
    }
  };

  const handleShareImage = async () => {
    if (!latestResult?.outputImageUrl) return;
    try {
      const res = await fetch(latestResult.outputImageUrl, { mode: "cors" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const filename = `vinci-${latestResult.persona.slug}-${ts}.png`;
      
      // On mobile with Web Share API support for files, use navigator.share()
      if (navigator.share && /Mobi|Android|iPhone/i.test(navigator.userAgent)) {
        try {
          const file = new File([blob], filename, { type: blob.type || "image/png" });
          await navigator.share({ files: [file] });
          toast.success("Shared");
          return;
        } catch (shareErr: unknown) {
          // User cancelled or share API not supported for files — fall through to URL copy
          if (shareErr instanceof Error && shareErr.name === "AbortError") return;
        }
      }
      
      // Fallback: copy the shareable URL to clipboard (desktop or mobile without Web Share API)
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(latestResult.outputImageUrl);
          toast.success("Link copied");
        }
      } catch {
        toast.error("Failed to copy link");
      }
    } catch (err) {
      console.warn("Image share failed", err);
      toast.error("Failed to share image");
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are supported");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File must be under 10 MB");
      return;
    }
    if (referenceImage?.startsWith("blob:")) URL.revokeObjectURL(referenceImage);
    setReferenceImage(URL.createObjectURL(file));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const maybeFireFirstTransformCelebration = useCallback(
    (message: string) => {
      try {
        if (sessionStorage.getItem("vml_first_transform_shown") !== "1") {
          sessionStorage.setItem("vml_first_transform_shown", "1");
          setCelebrationMessage(message);
          setCelebrationOpen(true);
        }
        if (
          !user &&
          selectedMode === "write" &&
          sessionStorage.getItem("vml_create_pulse_seen") !== "1"
        ) {
          setShowCreatePulse(true);
        }
      } catch {
        // sessionStorage may be unavailable (private mode); skip silently
      }
    },
    [user, selectedMode],
  );

  useEffect(() => {
    if (!celebrationOpen) return;
    const t = setTimeout(() => setCelebrationOpen(false), 5000);
    return () => clearTimeout(t);
  }, [celebrationOpen]);

  const markTransformProgress = useCallback((_mode: StudioSelectedMode) => {
    if (user) {
      supabase
        .from("profiles")
        .update({ has_transformed: true })
        .eq("user_id", user.id)
        .then(() => {});
    }
  }, [user]);

  const markSaveProgress = useCallback(() => {
    if (user) {
      supabase
        .from("profiles")
        .update({ has_saved: true })
        .eq("user_id", user.id)
        .then(() => {});
    }
  }, [user]);

  const handleSaveAllRepurpose = useCallback(async () => {
    if (!user) {
      setUnlockOpen(true);
      return;
    }
    const r = results.length > 0 ? results[Math.min(currentResultIndex, results.length - 1)] : null;
    if (!r?.outputText || !r.repurposePlatformIds?.length) return;
    const secs = parseRepurposeOutput(r.outputText, r.repurposePlatformIds);
    if (!secs.length) return;
    try {
      for (const sec of secs) {
        if (!sec.content.trim()) continue;
        const body =
          sec.platform === "Email" ? formatRepurposeEmailDisplay(sec.content) : sec.content;
        await supabase.from("saved_works").insert({
          user_id: user.id,
          persona_slug: r.persona.slug,
          persona_name: r.persona.name,
          intensity: 90,
          input_snippet: `${r.inputSnippet.slice(0, 200)} (${sec.platform})`.slice(0, 300),
          output_text: body.slice(0, 2000),
          work_type: "text",
          is_favorite: false,
        });
      }
      setSavedRefreshKey((k) => k + 1);
      markSaveProgress();
      toast.success("Saved all platforms to Workspace");
    } catch (err) {
      console.error("[handleSaveAllRepurpose] insert failed", err);
      toast.error("Failed to save — please try again");
    }
  }, [user, results, currentResultIndex, markSaveProgress]);

  /**
   * Per-platform Save for the Repurpose output panel. Inserts a saved_works
   * row for one platform's content, returning the new row id so the UI can
   * track which sections are saved and enable the favorite toggle on them.
   */
  const handleSaveRepurposeSection = useCallback(
    async (platform: string, content: string): Promise<string | null> => {
      if (!user) {
        setUnlockOpen(true);
        return null;
      }
      const r = results.length > 0 ? results[Math.min(currentResultIndex, results.length - 1)] : null;
      if (!r) return null;
      const body = platform === "Email" ? formatRepurposeEmailDisplay(content) : content;
      try {
        const { data, error } = await supabase
          .from("saved_works")
          .insert({
            user_id: user.id,
            persona_slug: r.persona.slug,
            persona_name: r.persona.name,
            intensity: 90,
            input_snippet: `${r.inputSnippet.slice(0, 200)} (${platform})`.slice(0, 300),
            output_text: body.slice(0, 2000),
            work_type: "text",
            is_favorite: false,
          })
          .select("id")
          .single();
        if (error) throw error;
        setSavedRefreshKey((k) => k + 1);
        return (data as { id?: string } | null)?.id ?? null;
      } catch (err) {
        console.error("[handleSaveRepurposeSection] insert failed", err);
        toast.error("Failed to save — please try again");
        return null;
      }
    },
    [user, results, currentResultIndex],
  );

  /**
   * Per-platform Favorite. If the section is already saved (existingId is
   * non-null), update is_favorite to the new value. If not yet saved, insert
   * a new row with is_favorite=true and return the new id so the UI can
   * thread it back. Returns the row id on success, null on failure.
   */
  const handleFavoriteRepurposeSection = useCallback(
    async (
      platform: string,
      content: string,
      existingId: string | null,
      nextFavorite: boolean,
    ): Promise<string | null> => {
      if (!user) {
        setUnlockOpen(true);
        return null;
      }
      if (existingId) {
        try {
          const { error } = await supabase
            .from("saved_works")
            .update({ is_favorite: nextFavorite })
            .eq("id", existingId);
          if (error) throw error;
          setSavedRefreshKey((k) => k + 1);
          return existingId;
        } catch (err) {
          console.error("[handleFavoriteRepurposeSection] update failed", err);
          toast.error("Failed to update favorite — please try again");
          return null;
        }
      }
      // Not yet saved — insert with is_favorite set to the desired state.
      const r = results.length > 0 ? results[Math.min(currentResultIndex, results.length - 1)] : null;
      if (!r) return null;
      const body = platform === "Email" ? formatRepurposeEmailDisplay(content) : content;
      try {
        const { data, error } = await supabase
          .from("saved_works")
          .insert({
            user_id: user.id,
            persona_slug: r.persona.slug,
            persona_name: r.persona.name,
            intensity: 90,
            input_snippet: `${r.inputSnippet.slice(0, 200)} (${platform})`.slice(0, 300),
            output_text: body.slice(0, 2000),
            work_type: "text",
            is_favorite: nextFavorite,
          })
          .select("id")
          .single();
        if (error) throw error;
        setSavedRefreshKey((k) => k + 1);
        return (data as { id?: string } | null)?.id ?? null;
      } catch (err) {
        console.error("[handleFavoriteRepurposeSection] insert failed", err);
        toast.error("Failed to favorite — please try again");
        return null;
      }
    },
    [user, results, currentResultIndex],
  );

  return (
    <div
      className="flex h-screen max-h-screen w-full max-w-full overflow-hidden"
      style={{ background: "#fafafb" }}
    >
      <StudioSidebar
        section={currentSection}
        onSelectSection={selectSection}
        collapsed={sidebarCollapsed}
        onCollapsedChange={setSidebarCollapsed}
      />
      <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden">
      <Navbar
        onUpgradeClick={() => setUpgradeOpen(true)}
        creditsUpdating={isLoading}
      />
      {(offlineBannerPhase === "shown" || offlineBannerPhase === "hiding") && (
        <div
          role="status"
          className="relative z-[60] shrink-0 px-4 py-2.5 text-sm font-medium text-slate-900 transition-opacity duration-200 ease-out"
          style={{
            background: "#FEF3C7",
            opacity: offlineBannerPhase === "hiding" ? 0 : 1,
            borderBottom: "1px solid rgba(234, 179, 8, 0.35)",
          }}
        >
          <div className="mx-auto flex max-w-7xl items-center gap-2">
            <WifiOff className="h-4 w-4 shrink-0 text-slate-700" aria-hidden />
            <span>You&apos;re offline. Transforms will resume when you reconnect.</span>
          </div>
        </div>
      )}
      {/* Guest credit banner removed to match Base44 layout. */}

      {activeTab === "saved" ? (
        <div
          data-vml-scroll-root
          className="relative z-10 flex-1 min-h-0 overflow-y-auto"
          style={{ paddingBottom: "calc(100px + env(safe-area-inset-bottom, 0px))" }}
        >
          <Suspense
            fallback={
              <div className="flex items-center justify-center py-12">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            }
          >
            <StudioModeErrorBoundary label="workspace">
              <WorkspacePanel
                onNavigate={(path) => navigate(path)}
                onSignOut={() => {
                  void signOut();
                  navigate("/", { replace: true });
                }}
                savedRefreshKey={savedRefreshKey}
              />
            </StudioModeErrorBoundary>
          </Suspense>
        </div>
      ) : activeTab === "ai-coach" ? (
        <div
          data-vml-scroll-root
          className="relative z-10 flex flex-1 min-h-0 flex-col"
          style={{ paddingBottom: "calc(100px + env(safe-area-inset-bottom, 0px))" }}
        >
          <Suspense
            fallback={
              <div className="flex items-center justify-center py-12">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            }
          >
            <StudioModeErrorBoundary label="ai-coach">
              <AICoachPanel />
            </StudioModeErrorBoundary>
          </Suspense>
        </div>
      ) : (
        <div
          data-vml-scroll-root
          className="relative z-10 flex-1 min-h-0 overflow-y-auto"
          style={{ paddingBottom: "calc(100px + env(safe-area-inset-bottom, 0px))" }}
        >

          {currentSection === "repurpose" ? (
            <StudioModeErrorBoundary label="repurpose">
              <Suspense
                fallback={
                  <div className="flex min-h-[200px] items-center justify-center py-12">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  </div>
                }
              >
                <RepurposeMode
                  inputText={inputText}
                  onInputChange={setInputText}
                  onSubmit={handleSubmit}
                  onClearInput={handleClearInput}
                  isSubmitDisabled={isSubmitDisabled}
                  isLoading={isLoading}
                  submitIdleLabel={submitIdleLabel}
                  submitLoadingLabel={submitLoadingLabel}
                  transformShortcutTitle={transformShortcutTitle}
                  copyShortcutTitle={copyShortcutTitle}
                  saveShortcutTitle={saveShortcutTitle}
                  selectedPlatforms={selectedPlatforms}
                  onPlatformsChange={setSelectedPlatforms}
                  outputText={createOutputText}
                  repurposeSections={createRepurposeSections}
                  onCopyRepurposeSection={handleCopyRepurposeSection}
                  onCopyOutput={handleCopy}
                  onSaveOutput={handleSave}
                  onSaveAllRepurpose={handleSaveAllRepurpose}
                  onSaveRepurposeSection={handleSaveRepurposeSection}
                  onFavoriteRepurposeSection={handleFavoriteRepurposeSection}
                  onRegenerate={handleSubmit}
                  onOpenCoach={openCoach}
                  isOutputCopied={copied}
                  isOutputSaved={saved}
                  onWritingSettingsChange={handleWritingSettingsChange}
                />
              </Suspense>
            </StudioModeErrorBoundary>
          ) : currentSection === "images" ? (
            <StudioModeErrorBoundary label="images">
              <Suspense
                fallback={
                  <div className="flex min-h-[200px] items-center justify-center py-12">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  </div>
                }
              >
                <ImagesMode
                  inputText={inputText}
                  onInputChange={setInputText}
                  onSubmit={handleSubmit}
                  onClearInput={handleClearInput}
                  isSubmitDisabled={isSubmitDisabled}
                  isLoading={isLoading}
                  submitIdleLabel={submitIdleLabel}
                  submitLoadingLabel={submitLoadingLabel}
                  transformShortcutTitle={transformShortcutTitle}
                  imagePersona={imagePersona}
                  onImagePersonaChange={setImagePersona}
                  imageOutputUrl={createImageOutputUrl}
                  createImageReferenceDataUrl={createImageReferenceDataUrl}
                  onCreateImageReferenceDataUrlChange={setCreateImageReferenceDataUrl}
                  onDownloadImage={handleDownload}
                  onCopyImage={handleCopyImage}
                  onShareImage={handleShareImage}
                  onRegenerate={handleSubmit}
                  onAspectRatioChange={setImageAspectRatio}
                  onMoodsChange={setImageMoods}
                  imageError={imageError}
                  onClearImageError={() => setImageError(null)}
                />
              </Suspense>
            </StudioModeErrorBoundary>
          ) : (
            <StudioModeErrorBoundary label="rewrite">
              <Suspense
                fallback={
                  <div className="flex min-h-[200px] items-center justify-center py-12">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  </div>
                }
              >
                <RewriteMode
                  inputText={inputText}
                  onInputChange={setInputText}
                  onSubmit={handleSubmit}
                  onClearInput={handleClearInput}
                  isSubmitDisabled={isSubmitDisabled}
                  isLoading={isLoading}
                  submitIdleLabel={submitIdleLabel}
                  submitLoadingLabel={submitLoadingLabel}
                  transformShortcutTitle={transformShortcutTitle}
                  clearShortcutTitle={clearShortcutTitle}
                  copyShortcutTitle={copyShortcutTitle}
                  saveShortcutTitle={saveShortcutTitle}
                  activeTab={rewriteTab}
                  onTabChange={setRewriteTab}
                  outputText={writeOutputText ?? createOutputText}
                  onCopyOutput={handleCopy}
                  onSaveOutput={handleSave}
                  onRegenerate={handleSubmit}
                  onOpenCoach={openCoach}
                  isOutputCopied={copied}
                  isOutputSaved={saved}
                  onWritingSettingsChange={handleWritingSettingsChange}
                />
              </Suspense>
            </StudioModeErrorBoundary>
          )}
        </div>
      )}

      {celebrationOpen && (
        <Suspense fallback={null}>
          <CelebrationToast
            open={celebrationOpen}
            message={celebrationMessage}
            onTryAnother={() => {
              setCelebrationOpen(false);
              const row = document.getElementById("try-another-voice");
              row?.scrollIntoView({ behavior: "smooth", block: "center" });
            }}
            onUseOwnText={() => {
              setCelebrationOpen(false);
              setInputText("");
              setTimeout(() => {
                const ta = document.getElementById("studio-input") as HTMLTextAreaElement | null;
                ta?.focus();
              }, 50);
            }}
          />
        </Suspense>
      )}
      <Suspense fallback={null}>
        <UpgradeModal open={upgradeOpen} onOpenChange={setUpgradeOpen} currentTier={credits?.tier} />
      </Suspense>
      <Suspense fallback={null}>
        <UnlockTrialModal
          open={unlockOpen}
          onOpenChange={setUnlockOpen}
          onBeforeRedirect={() =>
            savePendingInput(
              buildPendingPayload(inputText, referenceImage, selectedPersona, imagePersona, selectedMode, createSubTab, createPrompt, createOutputType),
            )
          }
        />
      </Suspense>
      {sidePanelChunkMounted && (
        <Suspense fallback={null}>
          <StudioSidePanel
            open={sidePanelOpen}
            onClose={() => setSidePanelOpen(false)}
            onLoadTransform={handleSidePanelLoad}
            projectId={searchParams.get("project")}
            refreshKey={sidePanelRefreshKey}
          />
        </Suspense>
      )}
      <BottomTabBar
        section={currentSection}
        onSelectSection={(next) => {
          if ((next === "repurpose" || next === "images") && showCreatePulse) {
            setShowCreatePulse(false);
            try {
              sessionStorage.setItem("vml_create_pulse_seen", "1");
            } catch {
              /* ignore */
            }
          }
          selectSection(next);
        }}
      />
      </div>
    </div>
  );
};

export default Studio;
